import { useEffect, useRef } from 'react'

import { themeTokens } from '../../content/siteContent.js'

const blobs = [
  { color: themeTokens.colors.violet, ax: 0.24, ay: 0.18, px: 0.2, py: 1.4, period: 52000, radius: 0.62 },
  { color: themeTokens.colors.cyan, ax: 0.2, ay: 0.16, px: 1.8, py: 0.4, period: 41000, radius: 0.52 },
  { color: themeTokens.colors.gold, ax: 0.18, ay: 0.14, px: 2.6, py: 2.1, period: 47000, radius: 0.42 },
  { color: themeTokens.colors.magenta, ax: 0.22, ay: 0.2, px: 3.2, py: 1.2, period: 36000, radius: 0.5 },
  { color: themeTokens.colors.indigo, ax: 0.28, ay: 0.13, px: 4.3, py: 2.8, period: 29000, radius: 0.58 },
]

export function AuroraField({ reducedMotion = false }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d', { alpha: true })
    const offscreen = document.createElement('canvas')
    const off = offscreen.getContext('2d', { alpha: true })
    let frame = 0
    let hidden = document.hidden

    const resize = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      context.setTransform(dpr, 0, 0, dpr, 0, 0)

      const lowWidth = 520
      offscreen.width = lowWidth
      offscreen.height = Math.max(320, Math.round(lowWidth * (height / Math.max(width, 1))))
    }

    const draw = (time = 0) => {
      const width = offscreen.width
      const height = offscreen.height
      const minSide = Math.min(width, height)
      const maxSide = Math.max(width, height)
      const breath = 1 + Math.sin(time / 12000) * 0.06

      off.clearRect(0, 0, width, height)
      off.fillStyle = themeTokens.colors.void
      off.fillRect(0, 0, width, height)
      off.globalCompositeOperation = 'screen'
      off.filter = 'blur(26px)'

      blobs.forEach((blob, index) => {
        const t = reducedMotion ? index * 0.4 : time / blob.period + index * 0.23
        const x = width * (0.5 + blob.ax * Math.sin(t * Math.PI * 2 + blob.px))
        const y = height * (0.48 + blob.ay * Math.sin(t * Math.PI * 2 * 1.37 + blob.py))
        const radius = maxSide * blob.radius * breath
        const gradient = off.createRadialGradient(x, y, minSide * 0.04, x, y, radius)
        gradient.addColorStop(0, `${blob.color}99`)
        gradient.addColorStop(0.46, `${blob.color}38`)
        gradient.addColorStop(1, `${blob.color}00`)
        off.fillStyle = gradient
        off.beginPath()
        off.arc(x, y, radius, 0, Math.PI * 2)
        off.fill()
      })

      off.filter = 'none'
      off.globalCompositeOperation = 'source-over'

      context.clearRect(0, 0, canvas.width, canvas.height)
      context.globalAlpha = 0.48
      context.drawImage(offscreen, 0, 0, window.innerWidth, window.innerHeight)
      context.globalAlpha = 1
    }

    const animate = (time) => {
      if (!hidden) draw(time)
      if (!reducedMotion) frame = requestAnimationFrame(animate)
    }

    const onVisibility = () => {
      hidden = document.hidden
    }

    resize()
    draw(0)
    if (!reducedMotion) frame = requestAnimationFrame(animate)
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [reducedMotion])

  return <canvas ref={canvasRef} className="aurora-field" aria-hidden="true" />
}
